import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import { toTitleCase } from "../../../shared/Functions";

export interface SortColumnOption {
  key: string;
  label: string;
}

interface FilterDropdownProps {
  isOpen: boolean;
  sortOrder: "asc" | "desc";
  sortColumns: SortColumnOption[];
  selectedSortColumn: string | null;
  onSortOrderChange: (order: "asc" | "desc") => void;
  onSortColumnSelect: (column: string | null) => void;
}

export const FilterDropdown = React.memo<FilterDropdownProps>(
  ({
    isOpen,
    sortOrder,
    sortColumns,
    selectedSortColumn,
    onSortOrderChange,
    onSortColumnSelect,
  }) => {
    if (!isOpen) return null;

    return (
      <div
        className="absolute right-0 top-6 w-60 max-h-[399px] bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-y-auto"
        data-testid="filter-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sort Direction Buttons */}
        <div className="p-3">
          <button
            onClick={() => onSortOrderChange("asc")}
            className={`w-full text-left p-2 rounded transition-colors ${
              sortOrder === "asc"
                ? "bg-gray-50 text-gray-700 border border-gray-200"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <BarsArrowUpIcon className="w-5 h-5 text-gray-700 stroke-2" />
              <span className="text-sm">Sort Ascending</span>
            </div>
          </button>
          <button
            onClick={() => onSortOrderChange("desc")}
            className={`w-full text-left p-2 rounded mt-1 transition-colors ${
              sortOrder === "desc"
                ? "bg-gray-50 text-gray-700 border border-gray-200"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <BarsArrowDownIcon className="w-5 h-5 text-gray-700 stroke-2" />
              <span className="text-sm">Sort Descending</span>
            </div>
          </button>
        </div>

        {/* Sort Columns - single select list */}
        <div className="pt-1 pb-3 px-3">
          <div className="text-xs text-[#979797] mb-2">Sort Columns</div>
          <div className="space-y-1">
            {sortColumns.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() =>
                  onSortColumnSelect(selectedSortColumn === key ? null : key)
                }
                data-testid={`sort-option-${key}`}
                className={`w-full text-left p-2 rounded text-sm transition-colors ${
                  selectedSortColumn === key
                    ? "bg-gray-100 text-gray-900 border border-gray-200 font-medium"
                    : "text-gray-700 hover:bg-gray-50 border border-transparent"
                }`}
              >
                {toTitleCase(label)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

FilterDropdown.displayName = "FilterDropdown";
